import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '@nw/ui';
import type { Space } from './spaces.js';

const WINDOW = 3; // dots shown on each side of the current space in the resting capsule

/**
 * The space scrubber, under your thumb at the bottom of the screen.
 * Resting: the current space and dots for its neighbours. Tap: all spaces. Swipe up: all spaces.
 * Slide along it: a strip of every space opens with a fisheye under your finger; release to go.
 * With many spaces the strip maps the finger's position across all of them, so a hundred
 * spaces are one slide away.
 */
export function Scrubber({ spaces, currentId, onGo, onOpenGrid }: { spaces: Space[]; currentId: string | null; onGo: (s: Space) => void; onOpenGrid: () => void }) {
  const idx = Math.max(
    0,
    spaces.findIndex((s) => s.id === currentId),
  );
  const current = currentId ? spaces[idx] : null;
  const [scrub, setScrub] = useState<{ hot: number; x: number } | null>(null);
  const g = useRef<{
    id: number;
    x0: number;
    y0: number;
    mode: 'tap' | 'scrub' | 'done';
  } | null>(null);
  const stripRef = useRef<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });
  const lastHot = useRef(-1);

  const stripWidth = () => Math.min(window.innerWidth - 32, Math.max(360, spaces.length * 76));
  const hotAt = (x: number) => {
    const { left, width } = stripRef.current;
    return Math.min(spaces.length - 1, Math.max(0, Math.floor(((x - left) / width) * spaces.length)));
  };

  useEffect(() => {
    if (!scrub) return;
    if (scrub.hot !== lastHot.current) {
      lastHot.current = scrub.hot;
      navigator.vibrate?.(4);
    }
  }, [scrub]);

  const Icon = current?.icon;
  const lo = Math.max(0, idx - WINDOW);
  const hi = Math.min(spaces.length - 1, idx + WINDOW);

  return (
    <>
      <button
        type="button"
        className="nw-scrubber"
        aria-label={`${current ? `In ${current.label}. ` : ''}Spaces: tap for all, slide to move between them`}
        aria-keyshortcuts="Control+J Meta+J"
        onPointerDown={(e) => {
          if (e.button > 0) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          g.current = {
            id: e.pointerId,
            x0: e.clientX,
            y0: e.clientY,
            mode: 'tap',
          };
        }}
        onPointerMove={(e) => {
          const s = g.current;
          if (!s || s.id !== e.pointerId || s.mode === 'done') return;
          const dx = e.clientX - s.x0;
          const dy = e.clientY - s.y0;
          if (s.mode === 'tap') {
            if (dy < -36 && Math.abs(dy) > Math.abs(dx)) {
              s.mode = 'done';
              onOpenGrid();
              return;
            }
            if (Math.abs(dx) < 10) return;
            s.mode = 'scrub';
            const width = stripWidth();
            stripRef.current = { left: (window.innerWidth - width) / 2, width };
            lastHot.current = idx;
          }
          setScrub({ hot: hotAt(e.clientX), x: e.clientX });
        }}
        onPointerUp={(e) => {
          const s = g.current;
          g.current = null;
          if (!s || s.id !== e.pointerId) return;
          if (s.mode === 'tap') onOpenGrid();
          else if (s.mode === 'scrub') {
            const target = spaces[hotAt(e.clientX)];
            setScrub(null);
            if (target && target.id !== currentId) onGo(target);
          }
        }}
        onPointerCancel={() => {
          g.current = null;
          setScrub(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' && spaces[idx + 1]) {
            e.preventDefault();
            onGo(spaces[idx + 1]!);
          } else if (e.key === 'ArrowLeft' && idx > 0 && spaces[idx - 1]) {
            e.preventDefault();
            onGo(spaces[idx - 1]!);
          }
        }}
      >
        {lo > 0 ? <span className="nw-scrub-more">{lo}</span> : null}
        {spaces.slice(lo, hi + 1).map((s, i) =>
          s.id === currentId ? (
            <span key={s.id} className="nw-scrub-current">
              {Icon ? <Icon className="size-[18px]" /> : null}
              <span>{s.label}</span>
            </span>
          ) : (
            <span key={s.id} className={cn('nw-scrub-dot', Math.abs(lo + i - idx) > 2 && 'nw-scrub-dot-far')} style={{ ['--dot-h' as string]: s.hue }} />
          ),
        )}
        {!current ? <span className="nw-scrub-current">Spaces</span> : null}
        {hi < spaces.length - 1 ? <span className="nw-scrub-more">{spaces.length - 1 - hi}</span> : null}
      </button>
      {createPortal(<AnimatePresence>{scrub ? <ScrubStrip key="strip" spaces={spaces} hot={scrub.hot} currentId={currentId} left={stripRef.current.left} width={stripRef.current.width} /> : null}</AnimatePresence>, document.body)}
    </>
  );
}

function ScrubStrip({ spaces, hot, currentId, left, width }: { spaces: Space[]; hot: number; currentId: string | null; left: number; width: number }) {
  const n = spaces.length;
  const slot = width / n;
  const hotSpace = spaces[hot]!;
  const HotIcon = hotSpace.icon;
  return (
    <motion.div
      className="nw-scrub-strip"
      style={{ left, width }}
      initial={{ opacity: 0, y: 24, scaleY: 0.6 }}
      animate={{ opacity: 1, y: 0, scaleY: 1 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ type: 'spring', stiffness: 520, damping: 34 }}
      aria-hidden
    >
      <motion.div className="nw-scrub-bubble" animate={{ x: slot * (hot + 0.5) }} transition={{ type: 'spring', stiffness: 700, damping: 40 }}>
        <span className="nw-scrub-bubble-icon" style={{ ['--dot-h' as string]: hotSpace.hue }}>
          <HotIcon className="size-5" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-semibold">{hotSpace.label}</span>
          <span className="block truncate text-[11px] opacity-70">{hotSpace.category}</span>
        </span>
      </motion.div>
      {spaces.map((s, i) => {
        const d = Math.abs(i - hot);
        const near = d <= 3 || slot >= 44;
        const scale = 1 + 0.6 * Math.max(0, 1 - d / 2.4);
        const Icon = s.icon;
        const newCategory = i > 0 && spaces[i - 1]!.category !== s.category;
        return (
          <span key={s.id} className={cn('nw-scrub-slot', newCategory && 'nw-scrub-slot-cat')} style={{ width: slot }}>
            {near ? (
              <motion.span
                className={cn('nw-scrub-icon', i === hot && 'nw-scrub-icon-hot', s.id === currentId && 'nw-scrub-icon-current')}
                style={{ ['--dot-h' as string]: s.hue }}
                animate={{ scale, y: -(scale - 1) * 26 }}
                transition={{ type: 'spring', stiffness: 700, damping: 38 }}
              >
                <Icon className="size-[18px]" />
              </motion.span>
            ) : (
              <span className="nw-scrub-tick" style={{ ['--dot-h' as string]: s.hue }} />
            )}
          </span>
        );
      })}
    </motion.div>
  );
}
